// laborSearchEngine.js
// Client-side scoring engine for LABOR items only — ported from the
// ItemSearchHelper3 / COItemSearchHelper scoring engine, stripped of the
// AI-intent server call and material-only fields. Drop next to
// ModuleBuilderPage.jsx and import { scoreLaborItem, tokenise, normalise }.

// ── Basic normalisation ─────────────────────────────────────────────────────

const FRACTION_RE = /\d+[-–]\d+\/\d+|\d+\/\d+/g;

function normaliseFractions(str) {
  return (str || "").replace(FRACTION_RE, (m) => m.replace(/[\s\-\/]/g, "_"));
}

export function normalise(str) {
  return normaliseFractions(str || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function tokenise(str) {
  return normalise(str)
    .split(/[\s\-\/,]+/)
    .filter(Boolean);
}

// ── Aliases (labor-relevant subset: gauges, fractions, trade shorthand) ────

const ALIASES = {
  "1/2": ["half", '1/2"', "half-inch", "0.5"],
  "5/8": ['5/8"', "five-eighth", "0.625"],
  "3/4": ['3/4"', "0.75", "three-quarter"],
  "1-5/8": ['1-5/8"', "1 5/8"],
  "3-5/8": ['3-5/8"', "3 5/8"],
  "2-1/2": ['2-1/2"', "2 1/2"],
  "25ga": ["25 ga", "25gauge", "25g"],
  "20ga": ["20 ga", "20gauge"],
  "18ga": ["18 ga", "18gauge"],
  "16ga": ["16 ga", "16gauge"],
  "14ga": ["14 ga", "14gauge"],
  dw: ["drywall", "dry wall"],
  gb: ["gypsum board", "gyp board"],
  mtl: ["metal"],
  std: ["stud", "studs"],
  ins: ["insulation"],
  trk: ["track"],
  hng: ["hang", "hanging"],
  fin: ["finish", "finishing"],
  tp: ["tape", "taping"],
  inst: ["install", "installation"],
};

// ── Product-type compounds relevant to labor descriptions ──────────────────

const PRODUCT_COMPOUNDS = [
  ["z", "bar"],
  ["z", "furring"],
  ["hat", "channel"],
  ["resilient", "channel"],
  ["rc", "channel"],
  ["corner", "bead"],
  ["l", "bead"],
  ["j", "bead"],
  ["j", "trim"],
  ["cee", "stud"],
  ["drywall", "screw"],
  ["type", "s"],
  ["type", "x"],
  ["tek", "screw"],
  ["lay", "in"],
  ["drop", "ceiling"],
  ["hang", "drywall"],
  ["tape", "mud"],
  ["scaffold", "erect"],
  ["punch", "list"],
];

function extractCompounds(str) {
  const toks = tokenise(str);
  const found = new Set();
  for (const [a, b] of PRODUCT_COMPOUNDS) {
    for (let i = 0; i < toks.length - 1; i++) {
      if (toks[i] === a && toks[i + 1] === b) found.add(`${a} ${b}`);
    }
    if (toks.includes(`${a}${b}`)) found.add(`${a} ${b}`);
  }
  return found;
}

// ── Dimensional phrase extraction (gauges, leg/flange sizes referenced in
//    labor descriptions like "Install 3-5/8 20ga Stud") ────────────────────

const DIM_DESCRIPTORS =
  "leg|stud|track|flange|web|wing|return|lip|face|bar|channel|bead";

const DIM_PHRASE_PATTERNS = [
  new RegExp(
    `(\\d+(?:[-–]\\d+\\/\\d+)?(?:\\/\\d+)?)["']?\\s*(${DIM_DESCRIPTORS})`,
    "gi",
  ),
  /(\d+)\s*(ga|gauge|mil)\b/gi,
  /(\d+\/\d+)["']/gi,
];

function extractDimPhrases(str) {
  const norm = normalise(str);
  const phrases = new Set();
  for (const re of DIM_PHRASE_PATTERNS) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(norm)) !== null) {
      const num = normaliseFractions(m[1])
        .replace(/[\s\-\/]/g, "_")
        .toLowerCase();
      const desc = (m[2] || "").toLowerCase();
      const d = desc === "gauge" ? "ga" : desc === "mil" ? "mil" : desc;
      phrases.add(d ? `${num} ${d}` : num);
    }
  }
  return phrases;
}

// ── Field extraction for a labor item ───────────────────────────────────────

function laborFields(item) {
  return {
    itemCode: normalise(item.itemCode || item.matCode || ""),
    description: normalise(
      item.description ||
        item.costCodeDescription ||
        item.itemDescription ||
        "",
    ),
    section: normalise(item.sectionNumber || item.costCode?.code || ""),
    rest: normalise([item.uom, item.itemNotes].filter(Boolean).join(" ")),
  };
}

// ── Core scorer — labor-only, no AI intent, no vendor/material fields ──────

export function scoreLaborItem(item, queryTokens, rawQuery = "") {
  if (!queryTokens.length) return { score: 0, matched: [] };

  const uniqueQueryTokens = [...new Set(queryTokens.map(normalise))].filter(
    Boolean,
  );

  const fields = laborFields(item);
  const descTokens = tokenise(fields.description);
  const allHaystackTokens = tokenise(Object.values(fields).join(" "));
  const allHaystack = Object.values(fields).join(" ");

  let score = 0;
  const matched = [];
  const unmatched = [];

  // ── Compound product/action matching (e.g. "hang drywall", "z bar") ──────
  const queryCompounds = extractCompounds(rawQuery);
  const descCompounds = extractCompounds(
    item.description || item.costCodeDescription || item.itemDescription || "",
  );

  let compoundScore = 0;
  const matchedCompoundTokens = new Set();

  for (const qc of queryCompounds) {
    if (descCompounds.has(qc)) {
      compoundScore += 90;
      qc.split(" ").forEach((t) => matchedCompoundTokens.add(t));
    } else {
      compoundScore -= 40;
    }
  }

  // ── Dimensional phrase matching (gauge/size mentioned in labor desc) ─────
  const queryDimPhrases = extractDimPhrases(rawQuery);
  const descDimPhrases = extractDimPhrases(
    item.description || item.costCodeDescription || item.itemDescription || "",
  );

  let dimScore = 0;
  const queryDescriptors = new Set();
  const queryDescriptorValues = new Map();

  for (const qp of queryDimPhrases) {
    const parts = qp.includes(" ") ? qp.split(" ") : [qp, ""];
    const val = parts[0];
    const desc = parts[1];
    if (desc) {
      queryDescriptors.add(desc);
      queryDescriptorValues.set(desc, val);
    }
    if (descDimPhrases.has(qp)) dimScore += 80;
  }

  for (const desc of queryDescriptors) {
    const qVal = queryDescriptorValues.get(desc);
    const candidateHasSameDescriptor = [...descDimPhrases].some((p) =>
      p.endsWith(" " + desc),
    );
    const candidateMatchesValue = descDimPhrases.has(`${qVal} ${desc}`);
    if (candidateHasSameDescriptor && !candidateMatchesValue) dimScore -= 60;
  }

  score += dimScore + compoundScore;

  // ── Token-level fuzzy scoring ──────────────────────────────────────────────
  for (const qtNorm of uniqueQueryTokens) {
    if (matchedCompoundTokens.has(qtNorm)) {
      matched.push(qtNorm);
      continue;
    }

    let hitScore = 0;
    const isShortNumeric = /^\d{1,2}$/.test(qtNorm);
    const isFractionToken = /_/.test(qtNorm);

    if (fields.itemCode === qtNorm) hitScore = 100;
    else if (
      !isShortNumeric &&
      qtNorm.length >= 3 &&
      fields.itemCode.startsWith(qtNorm)
    )
      hitScore = 60;
    else if (descTokens.includes(qtNorm)) hitScore = isShortNumeric ? 20 : 50;
    else if (allHaystackTokens.includes(qtNorm))
      hitScore = isShortNumeric ? 12 : 30;
    else {
      let aliasHit = false;
      for (const [canonical, aliases] of Object.entries(ALIASES)) {
        const allForms = [canonical, ...aliases].map(normalise);
        if (allForms.includes(qtNorm)) {
          if (
            allForms.some(
              (f) => allHaystackTokens.includes(f) || allHaystack.includes(f),
            )
          ) {
            hitScore = 20;
            aliasHit = true;
            break;
          }
        }
      }
      if (
        !aliasHit &&
        !isShortNumeric &&
        !isFractionToken &&
        qtNorm.length >= 3 &&
        allHaystack.includes(qtNorm)
      )
        hitScore = 5;
    }

    if (hitScore > 0) {
      score += hitScore;
      matched.push(qtNorm);
    } else {
      unmatched.push(qtNorm);
    }
  }

  // ── Unmatched token penalties ──────────────────────────────────────────────
  for (const ut of unmatched) {
    const isNumeric = /^\d+$/.test(ut);
    const isFraction = /_/.test(ut) || /\d+[-\/]\d/.test(ut);
    const base = isNumeric ? 10 : isFraction ? 25 : 25;
    score -= base;
  }

  // ── Bonuses ────────────────────────────────────────────────────────────────
  if (matched.length === uniqueQueryTokens.length) score += 30;

  const contentTokens = uniqueQueryTokens.filter((t) => !/^\d+$/.test(t));
  if (
    contentTokens.length > 0 &&
    contentTokens.every(
      (t) => descTokens.includes(t) || matchedCompoundTokens.has(t),
    )
  )
    score += 40;

  return { score, matched };
}

// ── Convenience: score + filter + sort a labor-only item list ──────────────

export function searchLaborItems(items, rawQuery, { sectionCode = "" } = {}) {
  const laborOnly = (items || []).filter(
    (i) => !i.category || i.category === "L",
  );

  const queryTokens = tokenise(rawQuery);

  if (!queryTokens.length) {
    // No query — prioritize section matches, then alphabetical by code
    return laborOnly.slice().sort((a, b) => {
      if (sectionCode) {
        const aSec =
          (a.sectionNumber || a.costCode?.code || "") === sectionCode;
        const bSec =
          (b.sectionNumber || b.costCode?.code || "") === sectionCode;
        if (aSec !== bSec) return aSec ? -1 : 1;
      }
      return (a.itemCode || "").localeCompare(b.itemCode || "");
    });
  }

  const scored = laborOnly
    .map((item) => ({ item, ...scoreLaborItem(item, queryTokens, rawQuery) }))
    .filter((r) => r.score > 0);

  // Section-match bonus applied after base scoring so it nudges ties without
  // overriding a strong text match from another section.
  if (sectionCode) {
    for (const r of scored) {
      const sec = r.item.sectionNumber || r.item.costCode?.code || "";
      if (sec === sectionCode) r.score += 35;
    }
  }

  return scored.sort((a, b) => b.score - a.score).map((r) => r.item);
}
